import { Offcanvas, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { AddEntityDrawerProps } from './EntityListProps';
import { useCreateIncidentResourceRequirement } from './hooks/incidentResourceRequirementHooks';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import FormTextField from './formFields/FormTextField';
import FormTextAreaField from './formFields/FormTextAreaField';
import CustomResourceTypeAddDrawer from './CustomResourceTypeAddDrawer';
import { useActualIncidentId } from './hooks/commonHooks';
import ResourceSelectFields from './ResourceSelectFields';
import { useCustomResourcePersistence } from './hooks/customResourceTypeHooks';

type FormFields = {
  name: string;
  description: string;
  kind: string;
  type: string;
  unit?: string;
  required_quantity: number;
  archived?: boolean;
  arrived?: boolean;
};

const IncidentResourceRequirementAddDrawer: React.FC<AddEntityDrawerProps> = ({
  toggle,
}) => {
  const form = useForm<FormFields>({ mode: 'onChange' });
  const { watch } = form;

  const watchedName = watch('name');
  const watchedKind = watch('kind');
  const watchedType = watch('type');
  const watchedRequiredQuantity = watch('required_quantity');

  const [showCustomResourceDrawer, setShowCustomResourceDrawer] =
    useState(false); // State to manage the custom resource drawer visibility

  const mutation = useCreateIncidentResourceRequirement();
  const { persistPendingResources } = useCustomResourcePersistence();

  const incidentId = useActualIncidentId();

  const onSubmit = async (formData: FormFields) => {
    await persistPendingResources(formData.kind, formData.type);
    await onNewResourceRequirementDataSubmit(formData);
  };

  const onNewResourceRequirementDataSubmit = async (formData: FormFields) => {
    const payload = {
      ...formData,
      archived: formData.archived ?? false,
      arrived: formData.arrived ?? false,
      incident_id: incidentId,
    };
    await mutation.mutateAsync(payload);
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Add Incident Resource Requirement">
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 255,
                    message: 'This maximum length is 255 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Description"
                fieldName="description"
                rules={{
                  maxLength: {
                    value: 300,
                    message: 'This maximum length is 300 characters',
                  },
                }}
                variant="full"
              />
            </Row>
            <ResourceSelectFields labels />
            <Row>
              <FormTextField
                title="Unit"
                fieldName="unit"
                rules={{
                  maxLength: {
                    value: 255,
                    message: 'This maximum length is 255 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextField
                title="Required Quantity"
                fieldName="required_quantity"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  pattern: {
                    value: /^\d+$/,
                    message: 'This field must be a positive integer',
                  },
                }}
                variant="half"
              />
            </Row>
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={
                  !watchedName ||
                  !watchedKind ||
                  !watchedType ||
                  !watchedRequiredQuantity ||
                  mutation.isPending
                }
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
      {showCustomResourceDrawer && (
        <CustomResourceTypeAddDrawer
          toggle={() => setShowCustomResourceDrawer(false)}
        />
      )}
    </DrawerWrapper>
  );
};

export default IncidentResourceRequirementAddDrawer;
